import {useTranslation} from 'react-i18next'
import {Location, LocationTranslations} from '../../../common/types/location'
import {SUPPORTED_LOCATION_TRANSLATION_LANGUAGES, SupportedLocationTranslationLanguage} from '../../location/translationLanguages'

const isMeaningful = (s: string | null | undefined): s is string => typeof s === 'string' && s.trim() !== ''

// Per-location all-or-nothing: a location's `translations` row claims the location for translation
// purposes only if at least one language has a non-empty description. Empty/missing rows defer to
// the i18n catalog. See doc/location-translations-context.md (Decision 2).
const rowHasMeaningfulTranslations = (translations: LocationTranslations | undefined): boolean => {
  if (!translations) return false
  return SUPPORTED_LOCATION_TRANSLATION_LANGUAGES.some((lng) => isMeaningful(translations[lng]?.description))
}

const resolveFromRow = (translations: LocationTranslations, lng: string, fallbackDescription: string | undefined): string => {
  const direct = translations[lng as keyof LocationTranslations]?.description
  if (isMeaningful(direct)) return direct
  const englishFallback = translations.en?.description
  if (isMeaningful(englishFallback)) return englishFallback
  return fallbackDescription ?? ''
}

// `boxName` is the catalog key for the legacy `location.description.<box.name>` lookup. Once
// the catalog is retired (Step 8 of the location translations migration) this arg can be dropped.
export const useTranslatedLocation = () => {
  const {t} = useTranslation()
  return {
    tLocationDescription: (location: Location, boxName: string, lng: string = 'en') => {
      if (rowHasMeaningfulTranslations(location.translations)) {
        return resolveFromRow(location.translations!, lng, location.description)
      }
      return t('location.description.' + boxName, location.description ?? '', {lng})
    },
  }
}

// Edit-form pre-population resolver: row[lng] → catalog[lng] (→ scalar for English only).
// Distinct from `useTranslatedLocation` because the read-side hook uses an English fallback within
// the row when the requested language is missing — that's correct for display but wrong for
// pre-populating an edit field, since the user would inadvertently re-save English text under a
// non-English language label.
export const useEditFormLocationDescription = () => {
  const {i18n} = useTranslation()
  return (location: Location, boxName: string, lng: SupportedLocationTranslationLanguage): string => {
    const fromRow = location.translations?.[lng]?.description
    if (isMeaningful(fromRow)) return fromRow
    const fromCatalog = i18n.getResource(lng, 'translation', `location.description.${boxName}`)
    if (typeof fromCatalog === 'string' && isMeaningful(fromCatalog)) return fromCatalog
    if (lng === 'en' && isMeaningful(location.description)) return location.description
    return ''
  }
}
